import { useEffect, useState } from 'react';

interface HeroProps {
  scrollToSection: (id: string) => void;
}

export default function Hero({ scrollToSection }: HeroProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/7e19d71ec04151ee91edd5deab0e2cc0.jpg"
          alt="Divine Sky Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-20 py-32">
        <div className="grid grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <i className="ri-book-open-line text-[#C9A961]"></i>
              <span className="text-sm font-medium whitespace-nowrap">New Release 2024</span>
            </div>
            
            <h1 className="text-8xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Gen 1
            </h1>
            
            <p className="text-2xl mb-4 font-light">by Richard Pontefract</p>
            
            <p className="text-xl mb-12 leading-relaxed max-w-xl" style={{ lineHeight: '1.8' }}>
              The entire Bible condensed into a 1-4 hour read. 
              30+ years of study distilled for modern readers who want to understand the complete story.
            </p>

            <div className="flex gap-4">
              <a 
                href="https://www.amazon.com/Gen-1-Richard-Pontefract/dp/1967361444"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-[#C9A961] text-white rounded-full font-medium transition-all duration-300 hover:bg-[#B89851] hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Buy on Amazon
                <i className="ri-amazon-line ml-2"></i>
              </a>
              <button 
                onClick={() => scrollToSection('works')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-medium transition-all duration-300 hover:bg-white/20 whitespace-nowrap cursor-pointer"
              >
                Learn More
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            <div className="flex gap-12 mt-16 pt-8 border-t border-white/20">
              <div>
                <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>30+</div>
                <div className="text-sm text-white/80">Years Research</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>3x</div>
                <div className="text-sm text-white/80">Bible Readings</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>1-4h</div>
                <div className="text-sm text-white/80">Reading Time</div>
              </div>
            </div>
          </div>

          {/* Right - Book Cover with Animation */}
          <div className="flex items-center justify-center">
            <div 
              className={`relative transition-all duration-1000 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                animation: isVisible ? 'float 6s ease-in-out infinite' : 'none'
              }}
            >
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-[#C9A961]/20 blur-3xl rounded-3xl scale-110"></div>
                
                {/* Book Cover */}
                <div className="relative w-[400px] h-[600px] rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <img 
                    src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/55b6a42b0ea2865e6f74ded7214c0195.jpeg"
                    alt="Gen 1 Book Cover"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#C9A961]/30 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10">
        <button 
          onClick={() => scrollToSection('about')}
          className="flex flex-col items-center gap-2 text-white/80 hover:text-white transition-colors cursor-pointer"
        >
          <span className="text-sm font-light whitespace-nowrap">Scroll to explore</span>
          <i className="ri-arrow-down-line text-2xl animate-bounce"></i>
        </button>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </section>
  );
}
